import React, { useEffect } from "react";

import enterpriseImage from "../assets/enterprise-solution.png";
import softwareImage from "../assets/saas-solutions-bg.png";
import consultingImage from "../assets/its-consulting.png";

import improvedEfficiency from "../assets/improved-efficiency.png";
import scalableSolutions from "../assets/scalable-solutions.png";
import betterWorkflows from "../assets/better-workflows.png";
import businessGrowth from "../assets/business-growth.png";

import {
  FaBuilding,
  FaCloud,
  FaCogs,
  FaSearch,
  FaLightbulb,
  FaCode,
  FaRocket,
  FaHeadset,
  FaBullseye,
  FaChartLine,
  FaUsers,
  FaShieldAlt,
  

} from "react-icons/fa";


import "./Services.css";

const Services = () => {
  /* =====================================================
     SCROLL ANIMATION
  ===================================================== */

  useEffect(() => {
    const animatedElements = document.querySelectorAll(".service-animate");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("service-visible");

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
      },
    );

    animatedElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);
  

  const benefitImages = [
  improvedEfficiency,
  scalableSolutions,
  betterWorkflows,
  businessGrowth,
];

  /* =====================================================
     SERVICES DATAs
  ===================================================== */

  const services = [
    {
      icon: <FaBuilding />,
      title: "Enterprise Solutions",
      description:
        "Custom enterprise systems to simplify business operations and improve efficiency.",
      items: [
        "CRM Solutions",
        "ERP Solutions",
        "Business Management Systems",
        "Workflow Automation",
        "Data & Reporting",
      ],
      image: enterpriseImage,
      imageAlt: "Enterprise Solutions",
    },

    {
      icon: <FaCloud />,
      title: "SaaS Solutions",
      description:
        "Deliver flexible, cloud-based software solutions that help businesses improve efficiency and scale with ease.",
      items: [
        "Cloud-Based Applications",
        "Subscription-Based Software",
        "Multi-Tenant Solutions",
        "SaaS Platform Integration",
        "Maintenance & Support",
      ],
      image: softwareImage,
      imageAlt: "SaaS Solutions",
    },

    {
      icon: <FaCogs />,
      title: "IT Consulting",
      description:
        "Expert guidance to help you choose the right technology, solve challenges and drive digital transformation.",
      items: [
        "Technology Consulting",
        "Digital Transformation",
        "System Analysis",
        "Architecture Planning",
        "Technical Support",
      ],
      image: consultingImage,
      imageAlt: "IT Consulting",
    },
  ];

  /* =====================================================
     PROCESS DATAs
  ===================================================== */

  const processSteps = [
    {
      icon: <FaSearch />,
      title: "Product Features",
      description:
        "Explore innovative features designed for your business needs.",
    },

    {
      icon: <FaLightbulb />,
      title: "Plan",
      description: "We create the right strategy and technology plan.",
    },

    {
      icon: <FaCode />,
      title: "Develop",
      description: "We build reliable and scalable solutions.",
    },

    {
      icon: <FaRocket />,
      title: "Deployment",
      description: "We deploy solutions for seamless business operations.",
    },

    {
      icon: <FaHeadset />,
      title: "Support",
      description: "We provide ongoing support and improvement.",
    },
  ];

  /* =====================================================
     LEARN MORE
  ===================================================== */

  const handleLearnMore = (serviceName) => {
    console.log(`Learn More clicked: ${serviceName}`);

    // ഇവിടെ later navigation add ചെയ്യാം.
    // Example:
    // navigate("/services/enterprise-solutions");
  };

  const businessBenefits = [
  {
    icon: <FaChartLine />,
    title: "Improved Efficiency",
    description:
      "Automate processes, reduce manual work and increase productivity across your business.",
  },
  {
    icon: <FaCloud />,
    title: "Scalable Solutions",
    description:
      "Flexible and future-ready solutions that grow with your business needs.",
  },
  {
    icon: <FaCogs />,
    title: "Better Workflows",
    description:
      "Streamline operations with intelligent systems and seamless integration.",
  },
  {
    icon: <FaBullseye />,
    title: "Business Growth",
    description:
      "Gain valuable insights, make smarter decisions and achieve long-term success.",
  },
];

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="services-page">
      {/* =================================================
          HERO
      ================================================= */}

      <section className="services-hero">
        <div className="services-hero-content service-animate">
          <span className="services-label">OUR SERVICES</span>

          <h1>
            Transforming Business Ideas into <span>Smart, Scalable</span>{" "}
            Technology Solutions
          </h1>

          <p>
            We help businesses streamline operations, solve complex challenges
            and achieve growth through innovative technology solutions.
          </p>

          <button
            type="button"
            className="services-hero-btn"
            onClick={() => {
              document.querySelector(".core-services-section")?.scrollIntoView({
                behavior: "smooth",
              });
            }}
          >
            Let's Talk
            <span>→</span>
          </button>
        </div>
      </section>

      {/* =================================================
          CORE SERVICES
      ================================================= */}

      <section className="core-services-section">
        <div className="core-services-container">
          {/* SECTION LABEL */}

          <span className="core-services-label service-animate">
            OUR CORE SERVICES
          </span>

          {/* SECTION TITLE */}

          <h2 className="core-services-title service-animate">
            Services That Drive <span>Real Business Value</span>
          </h2>

          {/* SECTION DESCRIPTION */}

          <p className="core-services-intro service-animate">
            We deliver end-to-end technology solutions tailored to your business
            needs. Our services are designed to help you work smarter, grow
            faster and stay ahead in a digital world.
          </p>

          {/* =================================================
              SERVICE CARDS
          ================================================= */}

          <div className="core-services-grid">
            {services.map((service, index) => (
              <article
                className={`core-service-card service-animate service-card-${index + 1}`}
                key={index}
              >
                {/* ICON + TITLE */}

                <div className="core-service-heading">
                  <div className="core-service-icon">{service.icon}</div>

                  <h3>{service.title}</h3>
                </div>

                {/* CONTENT */}

                <div className="core-service-content">
                  {/* DESCRIPTION */}

                  <p>{service.description}</p>

                  {/* SERVICE LIST */}

                  <ul className="core-service-list">
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>

                  {/* LEARN MORE */}

                  <button
                    type="button"
                    className="core-service-learn-btn"
                    onClick={() => handleLearnMore(service.title)}
                  >
                    Learn More
                    <span>→</span>
                  </button>
                </div>

                {/* BACKGROUND IMAGE */}

                <img
                  src={service.image}
                  alt={service.imageAlt}
                  className="core-service-image"
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================
          OUR PROCESS
      ================================================= */}

      <section className="our-process-section">
        <div className="our-process-container">
          {/* =================================================
              PROCESS HEADER
          ================================================= */}

          <div className="our-process-header service-animate">
            <div>
              <span className="our-process-label">OUR PROCESS</span>

              <h2 className="our-process-title">
                How <span>We Work</span>
              </h2>
            </div>

            <p className="our-process-intro">
              We follow a structured and collaborative approach to ensure
              successful outcomes for every project.
            </p>
          </div>

          {/* =================================================
              PROCESS CARDS
          ================================================= */}

          <div className="our-process-grid">
            {processSteps.map((step, index) => (
              <React.Fragment key={index}>
                {/* PROCESS CARD */}

                <article
                  className={`process-step service-animate process-card-${index + 1}`}
                >
                  {/* ICON */}

                  <div className="process-icon">{step.icon}</div>

                  {/* TITLE */}

                  <h3>{step.title}</h3>

                  {/* DESCRIPTION */}

                  <p>{step.description}</p>
                </article>

                {/* ARROW */}

                {index < processSteps.length - 1 && (
                  <div className="process-arrow">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* =================================================
          WHY CHOOSE MINDSTREAMX
      ================================================= */}

      <section className="why-choose-section">
        <div className="why-choose-container">
          <span className="why-choose-label service-animate">
            WHY CHOOSE MINDSTREAMX
          </span>

          <h2 className="why-choose-title service-animate">
            Your Technology Partner <span>for Growth</span>
          </h2>

          <div className="why-choose-grid">
            <article className="why-choose-item service-animate why-card-1">
              <div className="why-choose-icon">
                <FaBullseye />
              </div>

              <h3>Business Focused</h3>

              <p>Solutions designed around real business needs.</p>
            </article>

            <article className="why-choose-item service-animate why-card-2">
              <div className="why-choose-icon">
                <FaChartLine />
              </div>

              <h3>Scalable Technology</h3>

              <p>Built to grow with your business.</p>
            </article>

            <article className="why-choose-item service-animate why-card-3">
              <div className="why-choose-icon">
                <FaUsers />
              </div>

              <h3>Custom Solutions</h3>

              <p>Tailored to your unique requirements.</p>
            </article>

            <article className="why-choose-item service-animate why-card-4">
              <div className="why-choose-icon">
                <FaShieldAlt />
              </div>

              <h3>Long-Term Support</h3>

              <p>We're with you beyond implementation.</p>
            </article>
          </div>
        </div>
      </section>

      {/* =================================================
          TECHNOLOGIES WE USE
      ================================================= 

      <section className="tech-stack-section">
        <div className="tech-stack-container">
          <div className="tech-stack-header">
            <div>
              <span className="tech-stack-label service-animate">
                TECHNOLOGIES WE USE
              </span>

              <h2 className="tech-stack-title service-animate">
                Built With <span>Modern Technologies</span>
              </h2>
            </div>

            <p className="tech-stack-intro">
              We work with industry-leading technologies to deliver secure,
              scalable and high-performance solutions.
            </p>
          </div>

          <div className="tech-stack-grid">
            <div className="tech-stack-card service-animate tech-card-1">
              <SiReact className="tech-stack-icon react-tech-icon" />

              <span>React</span>
            </div>

            <div className="tech-stack-card service-animate tech-card-2">
              <FaJava className="tech-stack-icon java-tech-icon" />

              <span>Java</span>
            </div>

            <div className="tech-stack-card service-animate tech-card-3">
              <SiSpringboot className="tech-stack-icon spring-tech-icon" />

              <span>Spring Boot</span>
            </div>

            <div className="tech-stack-card service-animate tech-card-4">
              <SiNodedotjs className="tech-stack-icon node-tech-icon" />

              <span>Node.js</span>
            </div>

            <div className="tech-stack-card service-animate tech-card-5">
              <FaDatabase className="tech-stack-icon postgres-tech-icon" />

              <span>PostgreSQL</span>
            </div>
          </div>
        </div>
      </section>*/}

{/* =================================================
    BUSINESS BENEFITS
================================================= */}

<section className="business-benefits-section">
  <div className="business-benefits-container">

    <div className="business-benefits-header service-animate">
      <span className="business-benefits-label">
        BUSINESS BENEFITS
      </span>

      <h2 className="business-benefits-title">
        Real Benefits for <span>Real Growth</span>
      </h2>

      <p className="business-benefits-intro">
        Our solutions are designed to help businesses work smarter,
        improve efficiency and achieve sustainable growth.
      </p>
    </div>

    <div className="business-benefits-grid">
      {businessBenefits.map((benefit, index) => (
        <article
          className={`business-benefit-card service-animate benefit-card-${index + 1}`}
          key={index}
        >
          <div className="business-benefit-icon">
            {benefit.icon}
          </div>

          <h3>{benefit.title}</h3>

          <p>{benefit.description}</p>

          <img
  src={benefitImages[index]}
  alt={benefit.title}
  className="benefit-image"
/>

          
        </article>
      ))}
    </div>

  </div>
</section>

    </div>
  );
};

export default Services;
